// components/DemoCard.jsx

import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

const DemoCard = ({ demo, onClick }) => {
    const navigate = useNavigate();

    // Safely handles the click.
    // Prioritizes a custom onClick, then the demo.path, then the slug-based path.
    const safeHandleClick = useCallback(() => {
        // Construct the navigation path. Default to a slug-based path if 'path' isn't provided.
        // The slug-based path uses the structure: /demos/SLUG
        const navPath = demo.path || (demo.slug ? `/demos/${demo.slug}` : null);

        if (onClick) {
            onClick(navPath);
        } else if (navPath) {
            navigate(navPath);
        }
    }, [demo.path, demo.slug, onClick, navigate]);

    // --- Data Properties ---
    // Use sensible defaults if data is missing
    const title = demo.title || 'Untitled Demo';
    const description = demo.short_text || demo.description || 'Explore this technical demonstration.';
    const category = demo.category || 'Tool';
    const date = demo.date || 'N/A';
    const imageUrl = demo.imageUrl || '/images/default-demos.webp';

    // --- Theme Utility Classes for easier maintenance ---
    const cardTitleClass = 'text-text-light dark:text-text-dark';
    const cardMutedTextClass = 'text-muted-light dark:text-muted-dark';
    const cardBorderClass = 'border-border-light dark:border-border-dark';

    return (
        <div
            className="w-full h-full"
        >
            <div
                className="card h-full cursor-pointer rounded-xl overflow-hidden bg-card-light dark:bg-card-dark duration-300 ease-in-out"
            >

                {/* Image Container */}
                <div className="card-img-container h-[200px] overflow-hidden"> {/* Using 200px like ArticleCard */}
                    <img
                        src={imageUrl}
                        alt={title}
                        // This class handles the scale-up effect on hover, just like the ArticleCard.
                        className="card-img w-full h-full object-cover transition-transform duration-500 ease-in-out hover:scale-[1.05]"
                        loading="lazy"
                        style={{
                            objectPosition: `center ${typeof demo.imageOffsetY === 'number'
                                    ? `${demo.imageOffsetY}%`
                                    : demo.imageOffsetY || '50%'
                                }`,
                        }}
                        // Add basic error handling for image loading
                        onError={e => {
                            e.currentTarget.onerror = null; // prevents infinite loop
                            e.currentTarget.src = '/images/default-demos.webp';
                        }}
                    />
                </div>

                {/* Card Body */}
                {/* Changed p-5 to p-4 for consistency with ArticleCard, but kept flex-1 */}
                <div className="card-body p-4 flex flex-col flex-1">

                    {/* Category Badge */}
                    <div className="flex justify-between items-center mb-1"> {/* mb-1 for ArticleCard consistency */}
                        <span className="badge badge-primary dark:badge-secondary inline-flex items-center px-3 py-1 text-xs font-medium rounded-full bg-primary/10 text-primary dark:bg-secondary/10 dark:text-secondary">
                            {category}
                        </span>
                    </div>

                    {/* Title */}
                    <h5 className={`card-title text-lg font-semibold mb-2 line-clamp-2 ${cardTitleClass}`}> {/* mb-2 for ArticleCard consistency */}
                        {title}
                    </h5>

                    {/* Description (Mapping article's short_text to demo's description) */}
                    {/* Added min-h-[3rem] for ArticleCard consistency (was min-h-[4.5rem]) */}
                    <p className={`card-text text-sm mb-3 ${cardMutedTextClass} line-clamp-3 min-h-[3rem] flex-1`}>
                        {description}
                    </p>

                    {/* Footer */}
                    {/* Changed pt-2 to mt-auto for ArticleCard consistency */}
                    <div className={`flex justify-between pt-2 items-center mt-auto border-t ${cardBorderClass}`}>
                        <small className={`text-muted-light dark:text-muted-dark text-xs`}>
                            {date}
                        </small>

                        {/* Button for secondary click action on footer */}
                        <button
                            onClick={e => {
                                // Stop propagation to prevent the card's main onClick from firing twice
                                e.stopPropagation();
                                safeHandleClick();
                            }}
                            className="text-sm font-medium text-primary dark:text-secondary hover:underline transition-colors"
                        >
                            View Demo &rarr; {/* Changed from 'Read Article' */}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default React.memo(DemoCard);